// /////////////////////////////////////////////////////////////
// [ファイル] MinimalPartyForm.tsx
// [概要] 建物関係者の「最小顧客登録」ダイアログ (絶対登録関係の入口)。
//        未登録の関係者をフリーテキストではなく顧客マスタへ最小項目で登録し、
//        呼び出し元 (建物詳細の関係者ダイアログ) へ作成済み顧客を返す。
//        必須 = 種別 + 氏名(姓/会社名)。住所は建物住所を初期値に編集可。
//        保存前に重複チェックを必ず実行し、候補がある場合は既存採用を第一選択で提示。
//        registration_source='building_party' を自動記録する (出所の事実記録、隔離はしない)。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import { validateJapanesePhone } from '../utils/validation';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const UPSERT_URL = ep('upsert_crm_customer');
const CHECK_DUP_IND_URL = ep('check_duplicate_individuals');
const CHECK_DUP_CORP_URL = ep('check_duplicate_corporates');

// 呼び出し元へ返す作成済み (または採用した既存) 顧客
export interface RegisteredParty {
  id: string;
  display_name: string;
  customer_type: 'individual' | 'corporate';
}

interface MinimalPartyFormProps {
  open: boolean;
  // 住所の初期値に使う建物情報 (null の場合は空から入力)
  building: { postal_code?: string; address_main?: string } | null;
  onClose: () => void;
  onRegistered: (party: RegisteredParty) => void;
}

export default function MinimalPartyForm({ open, building, onClose, onRegistered }: MinimalPartyFormProps) {
  const { user } = useAuth();
  const notify = useNotification();

  const [customerType, setCustomerType] = useState<'individual' | 'corporate'>('individual');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastNameKana, setLastNameKana] = useState('');
  const [firstNameKana, setFirstNameKana] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyNameKana, setCompanyNameKana] = useState('');
  const [phone, setPhone] = useState('');
  const [postNumber, setPostNumber] = useState('');
  const [addressMain, setAddressMain] = useState('');
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [saving, setSaving] = useState(false);
  // 重複チェック結果 (null=未実施 / 配列=候補提示中)
  const [dupCandidates, setDupCandidates] = useState<any[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setCustomerType('individual');
    setLastName('');
    setFirstName('');
    setLastNameKana('');
    setFirstNameKana('');
    setCompanyName('');
    setCompanyNameKana('');
    setPhone('');
    setPostNumber(building?.postal_code || '');
    setAddressMain(building?.address_main || '');
    setErrors({});
    setDupCandidates(null);
  }, [open, building]);

  const validate = (): boolean => {
    const e: { [k: string]: string } = {};
    if (customerType === 'individual') {
      if (!lastName.trim()) e.lastName = '姓は必須です';
    } else {
      if (!companyName.trim()) e.companyName = '会社名は必須です';
    }
    const pe = validateJapanesePhone(phone);
    if (pe) e.phone = pe;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // 保存前の重複チェック (必須通過ゲート)。候補があれば採用 UI を出して保存を保留する
  const runDupCheck = async (): Promise<any[]> => {
    if (customerType === 'individual') {
      const res = await axios.post(CHECK_DUP_IND_URL, {
        last_name: lastName.trim() || undefined,
        first_name: firstName.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      return res.data.status === 'success' ? (res.data.candidates || []) : [];
    }
    const res = await axios.post(CHECK_DUP_CORP_URL, {
      company_name: companyName.trim() || undefined,
      company_name_kana: companyNameKana.trim() || undefined,
      tel_number: phone.trim() || undefined,
    });
    return res.data.status === 'success' ? (res.data.candidates || []) : [];
  };

  const registerNew = async () => {
    setSaving(true);
    try {
      const data: any = {
        customer_type: customerType,
        post_number: postNumber.trim(),
        address_main: addressMain.trim(),
        registration_source: 'building_party',
        operator_id: user?.email,
      };
      if (customerType === 'individual') {
        data.last_name = lastName.trim();
        data.first_name = firstName.trim();
        data.last_name_kana = lastNameKana.trim();
        data.first_name_kana = firstNameKana.trim();
        data.phone = phone.trim();
      } else {
        data.company_name = companyName.trim();
        data.company_name_kana = companyNameKana.trim();
        data.tel_number = phone.trim();
      }
      const res = await axios.post(UPSERT_URL, { customerId: null, data });
      if (res.data.status === 'success' && res.data.customerId) {
        const displayName = customerType === 'individual'
          ? `${lastName.trim()} ${firstName.trim()}`.trim()
          : companyName.trim();
        notify.success(`関係先「${displayName}」を顧客マスタへ登録しました`);
        onRegistered({ id: res.data.customerId, display_name: displayName, customer_type: customerType });
      } else {
        notify.error(res.data.message || '登録に失敗しました');
      }
    } catch (e) {
      console.error('minimal party register error:', e);
      notify.error('登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const found = await runDupCheck();
      if (found.length > 0) {
        setDupCandidates(found);
        setSaving(false);
        return; // 候補を提示し保存を保留 (既存採用が第一選択)
      }
    } catch (e) {
      console.error('minimal party dup check error:', e);
      // 重複チェック失敗時は登録を止めず続行 (顧客フォームと同じ方針)
    }
    await registerNew();
  };

  const handleAdoptExisting = (candidate: any) => {
    onRegistered({
      id: candidate.id,
      display_name: candidate.display_name || candidate.company_name || '',
      customer_type: customerType,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '0.875rem',
          fontWeight: 700,
          py: 0.75,
          bgcolor: '#f1f5f9',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Icons.PersonAdd sx={{ fontSize: 18, color: blueGrey[500] }} />
        関係先の最小登録（顧客マスタへ登録して紐付け）
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ToggleButtonGroup
          value={customerType}
          exclusive
          size="small"
          onChange={(_, v) => {
            if (v) {
              setCustomerType(v);
              setDupCandidates(null);
            }
          }}
        >
          <ToggleButton value="individual" sx={{ textTransform: 'none', px: 1.5, py: 0.4 }}>個人</ToggleButton>
          <ToggleButton value="corporate" sx={{ textTransform: 'none', px: 1.5, py: 0.4 }}>法人</ToggleButton>
        </ToggleButtonGroup>

        {customerType === 'individual' ? (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                size="small"
                label="姓（必須）"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={{ flex: 1 }}
              />
              <TextField size="small" label="名" value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField size="small" label="姓カナ" value={lastNameKana} onChange={(e) => setLastNameKana(e.target.value)} sx={{ flex: 1 }} />
              <TextField size="small" label="名カナ" value={firstNameKana} onChange={(e) => setFirstNameKana(e.target.value)} sx={{ flex: 1 }} />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="会社名（必須）"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
            />
            <TextField size="small" fullWidth label="会社名カナ" value={companyNameKana} onChange={(e) => setCompanyNameKana(e.target.value)} />
          </Stack>
        )}

        <TextField
          size="small"
          label="電話番号"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="例: 092-123-4567"
          sx={{ width: 240 }}
        />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            size="small"
            label="郵便番号"
            value={postNumber}
            onChange={(e) => setPostNumber(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            fullWidth
            label="住所（建物住所を初期値・編集可）"
            value={addressMain}
            onChange={(e) => setAddressMain(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        {dupCandidates && dupCandidates.length > 0 && (
          <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              類似する既存顧客が見つかりました。二重登録を防ぐため、既存顧客の採用を推奨します。
            </Typography>
            <Stack spacing={0.75}>
              {dupCandidates.slice(0, 5).map((c: any) => (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" noWrap>
                    {c.display_name || c.company_name || c.id}
                    {c.address_main && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {c.address_main}
                      </Typography>
                    )}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => handleAdoptExisting(c)} sx={{ flexShrink: 0, textTransform: 'none' }}>
                    この顧客を採用
                  </Button>
                </Box>
              ))}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button size="small" color="warning" onClick={registerNew} disabled={saving} sx={{ textTransform: 'none' }}>
                重複ではないため新規登録する
              </Button>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
          disabled={saving || (dupCandidates !== null && dupCandidates.length > 0)}
        >
          {saving ? '処理中...' : '登録して紐付けへ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
